/**
 * Tenant theme application. The whole stylesheet is built on the HSL
 * `--primary` CSS variable (see `src/index.css`). When the request lands
 * on `bifx.localhost`, `/tenant/current` returns the tenant's hex
 * `primaryColor`; we convert it to HSL components and inject onto
 * `:root` so every `bg-primary`, gradient, button, and accent flips at
 * once without touching individual components.
 */

/** Parse a `#RRGGBB` string to {r,g,b} 0-255. */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

/**
 * RGB → HSL. Returns `{h: 0–360, s: 0–100, l: 0–100}` integers (rounded)
 * so the CSS variable lands in the same shape as the hand-authored
 * `--primary: 333 86% 56%`.
 */
function rgbToHsl(r: number, g: number, b: number) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
      case gn: h = (bn - rn) / d + 2; break;
      case bn: h = (rn - gn) / d + 4; break;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hexToHslString(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  return `${h} ${s}% ${l}%`;
}

/**
 * Apply (or clear) the tenant's primary color on the document root.
 * Also flips `--primary-fg` to white/black based on luminance so button
 * text stays readable on any brand color.
 */
export function applyTenantTheme(hexColor: string | null | undefined) {
  const root = document.documentElement;
  const vars = ["--primary", "--primary-fg", "--primary-soft", "--accent", "--accent-fg", "--ring"];
  if (!hexColor) {
    vars.forEach((v) => root.style.removeProperty(v));
    return;
  }
  const hsl = hexToHslString(hexColor);
  if (!hsl) return;
  const rgb = hexToRgb(hexColor)!;
  // Perceived luminance — picks dark text on a light/gold brand, white on dark.
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  const fg = luminance > 0.55 ? "0 0% 8%" : "0 0% 100%";

  root.style.setProperty("--primary", hsl);
  root.style.setProperty("--primary-fg", fg);
  // Soft variant for backgrounds (14% opacity, matches `--primary-soft` shape)
  root.style.setProperty("--primary-soft", `${hsl} / 0.14`);

  // Single-broker build: drive the WHOLE accent system off the brand
  // color so gradients, rings, glows and selection all match — otherwise
  // the stock violet `--accent` / magenta `--ring` linger and the theme
  // looks unchanged. Accent = a slightly deeper, richer shade of the same
  // hue for a two-tone gradient; ring = the brand color itself.
  const m = /^(\d+) (\d+)% (\d+)%$/.exec(hsl);
  if (m) {
    const h = Number(m[1]);
    const s = Math.min(100, Number(m[2]) + 6);
    const l = Math.max(0, Number(m[3]) - 12);
    root.style.setProperty("--accent", `${h} ${s}% ${l}%`);
  } else {
    root.style.setProperty("--accent", hsl);
  }
  root.style.setProperty("--accent-fg", fg);
  root.style.setProperty("--ring", hsl);
}
